import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/seo";
import { FiRefreshCw, FiArrowRight, FiCheck } from "@/lib/icons";
import { useRefinanceCalculator } from "./_hooks/useRefinanceCalculator";
import { cn } from "@/lib/utils";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  formatValue,
  highlight,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  formatValue: (v: number) => string;
  highlight?: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium">{label}</label>
        <span className={cn("text-lg font-bold", highlight ? "text-accent" : "text-primary")}>
          {formatValue(value)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
      />
    </div>
  );
}

export function RefinanceCalculatorPage() {
  const { inputs, results, updateInput } = useRefinanceCalculator();

  const isSaving = results.monthlyPaymentDifference > 0;

  return (
    <>
      <SEO
        title="Refinance Savings Calculator"
        description="Calculate how much you could save by refinancing your home loan to a lower interest rate."
        url="/calculators/refinance"
      />

      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex p-3 bg-accent/20 rounded-xl mb-6">
              <FiRefreshCw className="size-6 text-primary" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              Refinance Savings Calculator
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how much you could save by refinancing to a better rate. Compare your current
              loan with potential new options.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Current Loan */}
            <div className="bg-background rounded-3xl border border-border p-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 bg-muted rounded-lg">
                  <span className="text-muted-foreground font-bold text-sm">OLD</span>
                </div>
                <h2 className="font-serif text-xl">Your Current Loan</h2>
              </div>

              <div className="space-y-6">
                <SliderInput
                  label="Current Loan Balance"
                  value={inputs.currentBalance}
                  onChange={(v) => updateInput("currentBalance", v)}
                  min={50000}
                  max={2000000}
                  step={10000}
                  formatValue={formatCurrency}
                />

                <SliderInput
                  label="Current Interest Rate"
                  value={inputs.currentRate}
                  onChange={(v) => updateInput("currentRate", v)}
                  min={4}
                  max={10}
                  step={0.05}
                  formatValue={(v) => `${v.toFixed(2)}%`}
                />

                <SliderInput
                  label="Years Remaining"
                  value={inputs.currentTermRemaining}
                  onChange={(v) => updateInput("currentTermRemaining", v)}
                  min={1}
                  max={30}
                  step={1}
                  formatValue={(v) => `${v} years`}
                />

                <div className="bg-muted rounded-xl p-4 mt-6">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Monthly Payment</span>
                    <span className="font-bold text-foreground">
                      {formatCurrency(results.currentMonthlyPayment)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Total Interest</span>
                    <span className="font-bold text-foreground">
                      {formatCurrency(results.currentTotalInterest)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* New Loan */}
            <div className="bg-background rounded-3xl border-2 border-accent/30 p-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 bg-accent/20 rounded-lg">
                  <span className="text-accent font-bold text-sm">NEW</span>
                </div>
                <h2 className="font-serif text-xl">New Loan Details</h2>
              </div>

              <div className="space-y-6">
                <SliderInput
                  label="New Interest Rate"
                  value={inputs.newRate}
                  onChange={(v) => updateInput("newRate", v)}
                  min={4}
                  max={10}
                  step={0.05}
                  formatValue={(v) => `${v.toFixed(2)}%`}
                  highlight={inputs.newRate < inputs.currentRate}
                />

                <SliderInput
                  label="New Loan Term"
                  value={inputs.newTerm}
                  onChange={(v) => updateInput("newTerm", v)}
                  min={5}
                  max={30}
                  step={1}
                  formatValue={(v) => `${v} years`}
                />

                <SliderInput
                  label="Switching Costs"
                  value={inputs.switchingCosts}
                  onChange={(v) => updateInput("switchingCosts", v)}
                  min={0}
                  max={5000}
                  step={100}
                  formatValue={formatCurrency}
                />

                <SliderInput
                  label="Cash Out (Optional)"
                  value={inputs.cashOut}
                  onChange={(v) => updateInput("cashOut", v)}
                  min={0}
                  max={100000}
                  step={5000}
                  formatValue={formatCurrency}
                />

                <div className="bg-accent/10 rounded-xl p-4 mt-6">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">New Loan Amount</span>
                    <span className="font-bold">{formatCurrency(results.newLoanAmount)}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Monthly Payment</span>
                    <span className="font-bold text-accent">
                      {formatCurrency(results.newMonthlyPayment)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Total Interest</span>
                    <span className="font-bold text-accent">
                      {formatCurrency(results.newTotalInterest)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison */}
          <div className="mt-12 max-w-4xl mx-auto">
            <div className="bg-background rounded-3xl border border-border p-8">
              <h3 className="font-serif text-xl mb-6 text-center">Side by Side Comparison</h3>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium"></th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Current</th>
                      <th className="text-right py-3 px-4 font-medium text-accent">New</th>
                      <th className="text-right py-3 px-4 font-medium">Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4 text-muted-foreground">Interest Rate</td>
                      <td className="py-3 px-4 text-right">{inputs.currentRate.toFixed(2)}%</td>
                      <td className="py-3 px-4 text-right">{inputs.newRate.toFixed(2)}%</td>
                      <td className="py-3 px-4 text-right text-accent">
                        {(inputs.currentRate - inputs.newRate).toFixed(2)}% lower
                      </td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4 text-muted-foreground">Monthly Payment</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(results.currentMonthlyPayment)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(results.newMonthlyPayment)}</td>
                      <td className={cn("py-3 px-4 text-right", isSaving ? "text-accent" : "text-destructive")}>
                        {isSaving ? "-" : "+"}{formatCurrency(Math.abs(results.monthlyPaymentDifference))}
                      </td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4 text-muted-foreground">Total Interest</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(results.currentTotalInterest)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(results.newTotalInterest)}</td>
                      <td className={cn("py-3 px-4 text-right font-bold", results.totalInterestSaved > 0 ? "text-accent" : "text-destructive")}>
                        {results.totalInterestSaved > 0 ? "-" : "+"}{formatCurrency(Math.abs(results.totalInterestSaved))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {isSaving && (
                <div className="mt-8 text-center">
                  <Button asChild size="lg" className="btn-pill">
                    <Link to="/contact">
                      Start Your Refinance
                      <FiArrowRight className="ml-2 size-4" />
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Savings Banner - placed after inputs */}
          {isSaving && (
            <div className="bg-primary text-primary-foreground rounded-3xl p-8 mt-12 max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-primary-foreground/90 text-sm mb-1">Monthly Savings</p>
                  <p className="text-3xl font-bold">{formatCurrency(results.monthlyPaymentDifference)}</p>
                </div>
                <div>
                  <p className="text-primary-foreground/90 text-sm mb-1">Yearly Savings</p>
                  <p className="text-3xl font-bold">{formatCurrency(results.yearlysSavings)}</p>
                </div>
                <div>
                  <p className="text-primary-foreground/90 text-sm mb-1">Break Even</p>
                  <p className="text-3xl font-bold">{results.breakEvenMonths} months</p>
                </div>
              </div>
              <div className="text-center mt-6 pt-6 border-t border-primary-foreground/20">
                <p className="text-primary-foreground/90 text-sm">Total Interest Saved Over Loan Term</p>
                <p className="text-4xl font-bold mt-1">{formatCurrency(results.totalInterestSaved)}</p>
              </div>
            </div>
          )}

          {/* Benefits */}
          <div className="mt-12 max-w-4xl mx-auto">
            <h3 className="font-serif text-2xl text-center mb-8">Why Refinance?</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "Lower Rate", desc: "Reduce your interest rate and monthly payments" },
                { title: "Access Equity", desc: "Use your home equity for renovations or investments" },
                { title: "Better Features", desc: "Offset accounts, redraw facilities, and more flexibility" },
              ].map((benefit) => (
                <div key={benefit.title} className="bg-background rounded-xl p-6 text-center">
                  <FiCheck className="size-8 text-accent mx-auto mb-3" />
                  <h4 className="font-medium mb-2">{benefit.title}</h4>
                  <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-6">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> This calculator provides estimates only. Actual savings depend on
              your specific loan terms, fees, and eligibility. Switching costs may include discharge
              fees, application fees, and government charges. Contact us for a personalised assessment.
            </p>
            <p className="text-sm text-muted-foreground">
              <strong>Disclaimer:</strong> This page provides general information only and has been
              prepared without considering your objectives, financial situation or needs. We recommend
              that you consider whether it is appropriate for your circumstances and your full financial
              situation will need to be reviewed prior to acceptance of any offer or product. It does
              not constitute legal, tax or financial advice and you should always seek professional
              advice in relation to your individual circumstances.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
